 
 
import { Component, Input, input, output, signal, effect, computed, forwardRef, ViewChild, ElementRef } from '@angular/core';

import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';

export interface ComboBoxColumnSettings {
  field: string;
  title: string;
  width?: string;
}

@Component({
  selector: 'app-lt-multi-column-combo-box',
  imports: [
    FormsModule,
    MatAutocompleteModule,
    MatInputModule,
    MatFormFieldModule,
],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LTMultiColumnComboBoxComponent),
      multi: true,
    },
  ],
  template: `
    <mat-form-field [appearance]="fillMode" class="full-width">
      @if(label) {
        <mat-label>{{label}}</mat-label>
      }
      <input
        #inputField
        type="text"
        matInput
        [placeholder]="placeholder"
        [required]="required()"
        [disabled]="isDisabled()"
        [value]="filterText()"
        (input)="onInputChange($event)"
        (blur)="onTouched()"
        [matAutocomplete]="auto">
    
        <mat-autocomplete #auto="matAutocomplete" (optionSelected)="onOptionSelected($event)" [displayWith]="displayFn.bind(this)">
          <!-- Multi-column header -->
          @if (columns().length > 0) {
            <div class="column-header">
              @for (column of columns(); track column) {
                <div
                  class="column-title"
                  [style.width]="column.width || 'auto'">
                  {{ column.title }}
                </div>
              }
            </div>
          }
    
          <!-- Multi-column options -->
          @for (item of filteredItems(); track item) {
            <mat-option [value]="item">
              <div class="column-row">
                @for (column of columns(); track column) {
                  <div
                    class="column-cell"
                    [style.width]="column.width || 'auto'">
                    {{ item[column.field] }}
                  </div>
                }
              </div>
            </mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
    `,
  styles: `
    .full-width {
      width: 100%;
    }

    .column-header {
      display: flex;
      padding: 8px 16px;
      background-color: #f5f5f5;
      border-bottom: 1px solid #e0e0e0;
      font-weight: 500;
    }

    .column-title {
      padding: 0 8px;
    }

    .column-row {
      display: flex;
      width: 100%;
    }

    .column-cell {
      padding: 0 8px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    ::ng-deep .mat-mdc-autocomplete-panel {
      min-width: calc(100% + 100px) !important;
      margin-left: -50px;
    }
  `,
})
export class LTMultiColumnComboBoxComponent implements ControlValueAccessor {
  @ViewChild('inputField') inputField!: ElementRef;

  //#region Inputs
  @Input() label = '';
  @Input() placeholder = 'Search...';
  @Input() fillMode: 'outline' | 'fill' = 'outline';

  data = input<any[]>([]);
  textField = input<string>('text');
  valueField = input<string>('value');
  columns = input<ComboBoxColumnSettings[]>([]);
  required = input(false);

  //#region Disabled
  @Input() set disabled(isDisabled: boolean) {
    if (isDisabled) {
      this.inputControl.disable();
    }
    else {
      this.inputControl.enable();
    }
  }
  get disabled(): boolean {
    return this.inputControl.disabled;
  }
  //#endregion

  //#endregion Inputs

  // Form Control
  inputControl = new FormControl<any>('');

  // Outputs
  valueChange = output<any>();
  selectionChange = output<any>();

  // Signals
  selectedValue = signal<any>(null);
  selectedItem = signal<any>(null);
  filterText = signal<string>('');
  isDisabled = signal<boolean>(false);

  // computed properties
  displayField = computed(() => this.textField());
  idField = computed(() => this.valueField());

  filteredItems = computed(() => {
    const searchText = this.filterText().toLowerCase();
    if (!searchText) {
      return this.data();
    }
    return this.data().filter(item =>
      String(item[this.displayField()]).toLowerCase().includes(searchText),
    );
  });

  // ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor() {
    effect(() => {
      const value = this.selectedValue();
      if (value) {
        this.onChange(value);
        this.valueChange.emit(value);
      }
    });
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedItem = event.option.value;
    this.selectedItem.set(selectedItem);
    this.selectedValue.set(selectedItem[this.idField()]);
    this.filterText.set(selectedItem[this.displayField()]);
    this.selectionChange.emit(selectedItem);
  }

  onInputChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.filterText.set(value);

    // Clear selection if text doesn't match
    if (this.selectedItem() && this.selectedItem()[this.displayField()] !== value) {
      this.selectedItem.set(null);
      this.selectedValue.set(null);
    }
  }

  displayFn(item: any): string {
    return item ? item[this.displayField()] : '';
  }

  writeValue(value: any): void {
    this.selectedValue.set(value);

    // Find the corresponding item and update the display text
    if (value !== null && value !== undefined) {
      const item = this.data().find(item => item[this.idField()] === value);
      if (item) {
        this.selectedItem.set(item);
        this.filterText.set(item[this.displayField()]);
      }
    } else {
      this.filterText.set('');
      this.selectedItem.set(null);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }
}
